import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Switch, withRouter } from 'react-router-dom';
import { ManorProvider } from '@comparethemarketau/manor-provider';
import { useBrandingService } from './hooks/useBrandingService';
import { useBrandContentFactory } from './hooks/useBrandContentFactory';

/**
 * Wrap provided children with ManorProvider if running in browser, for SSR, render children.
 * @param props - props for the ManorProvider component
 * @param children - children to be rendered
 * @returns {*}
 */
// eslint-disable-next-line react/prop-types
const SafeManorProvider = ({ children, theme }) => (typeof window !== 'undefined' ? <ManorProvider theme={theme}>{children}</ManorProvider> : children);

/*
        This spinner shows before we get any response from the branding service
        So we need to have a reference to what colour to use for the spinner during this state
*/
const _App = (props) => {
    const { routes, history } = props;
    const [isLoading, setIsLoading] = useState(true);
    const { isBrandThemeSet, manorTheme } = useBrandingService();
    const { spinnerColour, spinnerType } = useBrandContentFactory();

    // Hide the loading spinner when the brand theme is set
    // This is returned from the useBrandingServiceHook after calling switchTheme
    useEffect(() => {
        if (isLoading && isBrandThemeSet) {
            setIsLoading(false);
        }
    }, [isBrandThemeSet]);

    const isDefaultSpinner = spinnerType() !== 'CircularProgress';

    // Dotted orbiting-dots spinner (for Choosi / iSelect brands).
    // Uses inline box-shadow + currentColor so no CSS-variable dependency before the
    // branding service responds. Color comes from spinnerColour().
    const dottedKeyframesStyle = `
        @keyframes initialLoad6 {
            0%     { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
            5%,95% { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
            10%,59%{ box-shadow: 0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em; }
            20%    { box-shadow: 0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em; }
            38%    { box-shadow: 0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em; }
            100%   { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
        }
        @keyframes initialRound {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    const dottedSpinnerStyle = {
        color: spinnerColour(),
        fontSize: '80px',
        width: '1.1em',
        height: '1.1em',
        borderRadius: '50%',
        position: 'relative',
        margin: '0 auto',
        transform: 'translateZ(0)',
        animation: 'initialLoad6 1.7s infinite ease, initialRound 1.7s infinite ease',
        boxSizing: 'content-box'
    };

    // Simple border-arc spinner (for CTM / CircularProgress brands).
    const ringKeyframesStyle = `
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    `;
    const ringSpinnerStyle = {
        margin: '0 auto',
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        border: `8.5px solid ${spinnerColour()}`,
        borderBottomColor: 'transparent',
        animation: 'spin 1s linear infinite',
        // Fixes the spinner from 'jumping' when the CSS loads
        boxSizing: 'content-box'
    };

    // Will break preload if allowed on results page
    if (isLoading && (history.location.pathname !== '/car-insurance/journey/results')
        && (history.location.pathname !== '/home-contents-insurance/journey/results')) {
        return (
            <div style={{
                textAlign: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginTop: '20vh',
                width: '100%',
                fontFamily: 'Arial, Helvetica, sans-serif'
            }}>
                <h2 style={{
                    marginBottom: '60px',
                    fontSize: '28px',
                    fontWeight: '600',
                    lineHeight: '136%'
                }}>
                    Just a moment...
                </h2>
                {isDefaultSpinner ? (
                    <div style={{ ...dottedSpinnerStyle }}>
                        <style>{ dottedKeyframesStyle }</style>
                    </div>
                ) : (
                    <div style={{ ...ringSpinnerStyle }}>
                        <style>{ ringKeyframesStyle }</style>
                    </div>
                )}
            </div>
        );
    }

    return (
        <SafeManorProvider theme={manorTheme}>
            <div style={ { height: '100%' } } key="appDiv">
                <Switch key="Switch">
                    { routes }
                </Switch>
            </div>
        </SafeManorProvider>
    );
};

_App.propTypes = {
    routes: PropTypes.array.isRequired,
    history: PropTypes.object
};

// withRouter is used so that the history, match and location are passed to the components for the matching route
// see https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/withRouter.md
export const App = withRouter(_App);
